"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"
import "./Messages.css"

const Messages = () => {
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messageContent, setMessageContent] = useState("")
  const [studentSearchTerm, setStudentSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    loadStudents()
  }, [])

  const loadStudents = async () => {
    try {
      setIsLoading(true)
      setError("")
      const students = await apiService.getStudents()
      // Convert students to conversation format
      const conversations = students.map(student => ({
        user: student,
        last_message: null // Will be populated when we load conversations
      }))
      setConversations(conversations)
      
      // Load conversations to get last messages
      const conversationsData = await apiService.getConversations()
      // Merge conversations data with students
      const mergedConversations = conversations.map(conv => {
        const conversationData = conversationsData.find(c => c.user.id === conv.user.id)
        return {
          ...conv,
          last_message: conversationData?.last_message || null
        }
      })
      setConversations(mergedConversations)
    } catch (error) {
      console.error('Error loading students:', error)
      setError(error.message || 'Failed to load students')
    } finally {
      setIsLoading(false)
    }
  }

  const loadConversationMessages = async (userId) => {
    try {
      const data = await apiService.getConversationWith(userId)
      setSelectedConversation(data)
    } catch (error) {
      console.error('Error loading conversation:', error)
      setError(error.message || 'Failed to load conversation')
    }
  }

  const handleStudentSelect = async (conversation) => {
    setSelectedConversation(null) // Clear current conversation while loading
    await loadConversationMessages(conversation.user.id)
  }

  const handleBackToList = () => {
    setSelectedConversation(null)
  }

  const handleStudentSearch = (e) => {
    setStudentSearchTerm(e.target.value)
  }

  const filteredConversations = conversations.filter((conversation) =>
    conversation.user.username.toLowerCase().includes(studentSearchTerm.toLowerCase())
  )

  const handleMessageChange = (e) => {
    setMessageContent(e.target.value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!selectedConversation || !messageContent.trim()) {
      alert("Please select a student and enter a message")
      return
    }

    try {
      const messageData = {
        receiver_id: selectedConversation.user.id,
        content: messageContent.trim()
      }

      await apiService.sendMessage(messageData)

      // Reset message input
      setMessageContent("")

      // Reload conversation to show the new message
      await loadConversationMessages(selectedConversation.user.id)
      // Reload conversations list to update last message
      await loadStudents()
    } catch (error) {
      console.error('Error sending message:', error)
      setError(error.message || 'Failed to send message')
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  if (isLoading) {
    return <div className="chat-loading">Loading conversations...</div>
  }

  if (error) {
    return <div className="chat-error">{error}</div>
  }

  return (
    <div className="chat-container">
      {!selectedConversation ? (
        // Chat List View
        <div className="chat-list-view">
          <div className="chat-header">
            <h2>Messages</h2>
            <div className="chat-search">
              <input
                type="text"
                placeholder="Search students..."
                value={studentSearchTerm}
                onChange={handleStudentSearch}
              />
            </div>
          </div>
          <div className="chat-list">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.user.id}
                className="chat-list-item"
                onClick={() => handleStudentSelect(conversation)}
              >
                <div className="chat-list-item-content">
                  <div className="chat-list-item-header">
                    <h4>{conversation.user.username}</h4>
                    {conversation.last_message && (
                      <span className="chat-time">
                        {formatDate(conversation.last_message.created_at)}
                      </span>
                    )}
                  </div>
                  {conversation.last_message && (
                    <p className="last-message">
                      {conversation.last_message.content.substring(0, 30)}
                      {conversation.last_message.content.length > 30 ? '...' : ''}
                    </p>
                  )}
            </div>
          </div>
        ))}
          </div>
        </div>
      ) : (
        // Chat Conversation View
        <div className="chat-conversation-view">
          <div className="chat-header">
            <button className="back-button" onClick={handleBackToList}>
              <i className="material-icons">arrow_back</i>
            </button>
            <h3>{selectedConversation.user.username}</h3>
          </div>

          <div className="chat-messages">
            {selectedConversation.messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender.id === selectedConversation.user.id ? 'received' : 'sent'}`}
            >
                <div className="message-content">
                  <p>{message.content}</p>
                  <span className="message-time">{formatDate(message.created_at)}</span>
                </div>
              </div>
            ))}
          </div>

          <form className="chat-input-form" onSubmit={handleSubmit}>
            <div className="chat-input-wrapper">
            <textarea
              value={messageContent}
              onChange={handleMessageChange}
                placeholder="Type your message..."
                rows="1"
              />
              <button type="submit" className="send-button">
            <i className="material-icons">send</i>
          </button>
            </div>
        </form>
      </div>
      )}
    </div>
  )
}

export default Messages
